/**
 * 밀봉비닐 CTA "배송비만 결제하고 밀봉비닐 받기" 클릭 시 검증 오케스트레이션.
 *
 * 신청 가능 → 재고 → 주소 순서로 검증한다. 앞 단계가 막히면 뒤 단계는 호출하지 않는다.
 *
 * 어댑터 주입 패턴 — 호출부를 갈아끼울 수 있도록 분리. 구현은 payment/api 참조.
 */

export interface CtaValidationDeps {
  /** 결제 완료된 활성 주문 없음 (true = 신청 가능) */
  checkAvailability: () => Promise<boolean>
  /** 봉투 재고/판매 여부 (true = 재고 있음 && 판매 중) */
  checkStock: () => Promise<boolean>
  /** 대표 지번 주소 등록 여부 (true = 등록됨) */
  checkAddressRegistered: () => Promise<boolean>
  /** 등록 주소가 배송 서비스 지역인지 (true = 배송 가능 지역) */
  checkServiceArea: () => Promise<boolean>
}

export type CtaValidationResult =
  | { type: 'pass' }
  | { type: 'alreadyApplied' }
  | { type: 'soldOut' }
  | { type: 'addressUnregistered' }
  | { type: 'serviceUnavailable' }

/**
 * CTA 클릭 시 가능여부·재고·주소등록·서비스지역을 순차 검증한다.
 *
 * - availability false(이미 결제 완료된 활성 주문 존재) → `alreadyApplied` (신청 정보 화면으로)
 * - 재고 NG → `soldOut`
 * - 주소 미등록 → `addressUnregistered`
 * - 등록 주소가 배송 불가 지역 → `serviceUnavailable`
 * - 전부 통과 → `pass`
 *
 * 앞 단계가 막히면 뒤 단계는 호출하지 않는다.
 * 어댑터가 reject 하면 에러를 그대로 전파한다 — 호출 측에서 재시도 안내 처리.
 */
export const validateCtaClick = async (deps: CtaValidationDeps): Promise<CtaValidationResult> => {
  const isAvailable = await deps.checkAvailability()
  if (!isAvailable) return { type: 'alreadyApplied' }

  const hasStock = await deps.checkStock()
  if (!hasStock) return { type: 'soldOut' }

  const hasAddress = await deps.checkAddressRegistered()
  if (!hasAddress) return { type: 'addressUnregistered' }

  const inServiceArea = await deps.checkServiceArea()
  if (!inServiceArea) return { type: 'serviceUnavailable' }

  return { type: 'pass' }
}
